// API service for cal.larryshelp.org scheduling platform

import {
  SchedulingUser,
  EventType,
  Availability,
  DayAvailability,
  BlackoutDate,
  Booking,
  BookingPageData,
  EventBookingPageData,
  AvailableSlot,
  CreateBookingRequest,
  BookingConfirmation,
  SchedulingStats,
  CalendarConnection,
  Workflow,
  PublicStaffMember
} from '../types/scheduling';

const API_BASE = '/backend/scheduling';

// Generic API client for scheduling endpoints (used by stub components)
export const schedulingApi = {
  async get(path: string) {
    const response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`GET ${path} failed: ${response.status}`);
    return response.json();
  },
  async post(path: string, body?: any) {
    const response = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: getAuthHeaders(), body: body ? JSON.stringify(body) : undefined });
    if (!response.ok) throw new Error(`POST ${path} failed: ${response.status}`);
    return response.json();
  },
  async delete(path: string) {
    const response = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`DELETE ${path} failed: ${response.status}`);
    return response.json();
  }
};

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// ============== PUBLIC ROUTES (No auth) ==============

export const getPublicBookingPage = async (subdomain: string): Promise<BookingPageData> => {
  const response = await fetch(`${API_BASE}/public/${subdomain}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('User not found');
  }

  return response.json();
};

export const getPublicEventType = async (subdomain: string, slug: string): Promise<EventBookingPageData> => {
  const response = await fetch(`${API_BASE}/public/${subdomain}/event/${slug}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('Event type not found');
  }

  return response.json();
};

export const getPublicStaff = async (subdomain: string): Promise<{ staff: PublicStaffMember[] }> => {
  const response = await fetch(`${API_BASE}/staff/public/${subdomain}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch staff');
  }

  return response.json();
};

export const getAvailableSlots = async (
  subdomain: string,
  slug: string,
  startDate?: string,
  endDate?: string,
  timezone?: string,
  staffId?: number
): Promise<{ availableSlots: AvailableSlot[]; timezone: string; eventDuration: number }> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (timezone) params.append('timezone', timezone);
  if (staffId) params.append('staffId', staffId.toString());

  const response = await fetch(`${API_BASE}/book/available-slots/${subdomain}/${slug}?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch available slots');
  }

  return response.json();
};

export const createBooking = async (
  subdomain: string,
  eventTypeSlug: string,
  data: CreateBookingRequest
): Promise<BookingConfirmation> => {
  const response = await fetch(`${API_BASE}/book/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subdomain,
      eventTypeSlug,
      ...data
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create booking');
  }

  return response.json();
};

export const cancelBooking = async (bookingUid: string, reason?: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/book/cancel/${bookingUid}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cancel booking');
  }
};

export const rescheduleBooking = async (bookingUid: string, newStartTime: string): Promise<BookingConfirmation> => {
  const response = await fetch(`${API_BASE}/book/reschedule/${bookingUid}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newStartTime })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reschedule booking');
  }

  return response.json();
};

export const lookupBooking = async (bookingUid: string): Promise<any> => {
  const response = await fetch(`${API_BASE}/book/lookup/${bookingUid}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error('Booking not found');
  }

  return response.json();
};

// ============== AUTHENTICATED ROUTES ==============

export const getSchedulingProfile = async (): Promise<SchedulingUser & { bookingUrl: string; needsSetup?: boolean }> => {
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const data = await response.json();
    if (data.needsSetup) {
      return { needsSetup: true } as any;
    }
    throw new Error('Failed to fetch profile');
  }

  return response.json();
};

export const setupSchedulingProfile = async (data: {
  subdomain: string;
  displayName?: string;
  bio?: string;
  timezone?: string;
}): Promise<{ id: number; subdomain: string; bookingUrl: string }> => {
  const response = await fetch(`${API_BASE}/profile/setup`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to setup profile');
  }

  return response.json();
};

export const updateSchedulingProfile = async (data: Partial<SchedulingUser>): Promise<void> => {
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update profile');
  }
};

// ============== EVENT TYPES ==============

export const getEventTypes = async (): Promise<{ eventTypes: EventType[] }> => {
  const response = await fetch(`${API_BASE}/event-types`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch event types');
  }

  return response.json();
};

export const createEventType = async (data: Partial<EventType>): Promise<{ id: number; slug: string; bookingUrl: string }> => {
  const response = await fetch(`${API_BASE}/event-types`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create event type');
  }

  return response.json();
};

export const updateEventType = async (id: number, data: Partial<EventType>): Promise<void> => {
  const response = await fetch(`${API_BASE}/event-types/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update event type');
  }
};

export const deleteEventType = async (id: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/event-types/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete event type');
  }
};

// ============== AVAILABILITY ==============

export const getAvailability = async (): Promise<{ timezone: string; weekStart: string; availability: DayAvailability[] }> => {
  const response = await fetch(`${API_BASE}/availability/`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch availability');
  }

  return response.json();
};

export const updateAvailability = async (data: { timezone?: string; availability: DayAvailability[] }): Promise<void> => {
  const response = await fetch(`${API_BASE}/availability/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update availability');
  }
};

export const getBlackoutDates = async (): Promise<{ blackoutDates: BlackoutDate[] }> => {
  const response = await fetch(`${API_BASE}/availability/blackout-dates`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch blackout dates');
  }

  return response.json();
};

export const createBlackoutDate = async (data: { startDate: string; endDate: string; reason?: string }): Promise<BlackoutDate> => {
  const response = await fetch(`${API_BASE}/availability/blackout-dates`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create blackout date');
  }

  return response.json();
};

export const deleteBlackoutDate = async (id: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/availability/blackout-dates/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete blackout date');
  }
};

// ============== DASHBOARD ==============

export const getSchedulingDashboard = async (): Promise<{
  profile: { subdomain: string; plan: string; bookingUrl: string };
  stats: SchedulingStats;
  upcomingBookings: Booking[];
  needsSetup?: boolean;
}> => {
  const response = await fetch(`${API_BASE}/dashboard`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const data = await response.json();
    if (data.needsSetup) {
      return { needsSetup: true } as any;
    }
    throw new Error('Failed to fetch dashboard');
  }

  return response.json();
};

// ============== CALENDAR INTEGRATION ==============

export const getCalendarStatus = async (): Promise<{
  google: CalendarConnection;
  microsoft: CalendarConnection;
}> => {
  const response = await fetch(`${API_BASE}/calendar/status`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch calendar status');
  }

  return response.json();
};

export const getGoogleAuthUrl = async (): Promise<{ authUrl: string }> => {
  const response = await fetch(`${API_BASE}/calendar/google/authorize`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to get Google auth URL');
  }

  return response.json();
};

export const connectGoogleCalendar = async (code: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/calendar/google/callback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ code })
  });

  if (!response.ok) {
    throw new Error('Failed to connect Google Calendar');
  }
};

export const disconnectGoogleCalendar = async (): Promise<void> => {
  const response = await fetch(`${API_BASE}/calendar/google/disconnect`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to disconnect Google Calendar');
  }
};

export const getMicrosoftAuthUrl = async (): Promise<{ authUrl: string }> => {
  const response = await fetch(`${API_BASE}/calendar/microsoft/authorize`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to get Microsoft auth URL');
  }

  return response.json();
};

export const connectMicrosoftCalendar = async (code: string): Promise<void> => {
  const response = await fetch(`${API_BASE}/calendar/microsoft/callback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ code })
  });

  if (!response.ok) {
    throw new Error('Failed to connect Microsoft Calendar');
  }
};

export const disconnectMicrosoftCalendar = async (): Promise<void> => {
  const response = await fetch(`${API_BASE}/calendar/microsoft/disconnect`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to disconnect Microsoft Calendar');
  }
};

// ============== WORKFLOWS ==============

export const getWorkflows = async (eventTypeId: number): Promise<{ workflows: Workflow[] }> => {
  const response = await fetch(`${API_BASE}/workflows/event-type/${eventTypeId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workflows');
  }

  return response.json();
};

export const createWorkflow = async (eventTypeId: number, data: Partial<Workflow>): Promise<{ id: number }> => {
  const response = await fetch(`${API_BASE}/workflows/event-type/${eventTypeId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create workflow');
  }

  return response.json();
};

export const updateWorkflow = async (workflowId: number, data: Partial<Workflow>): Promise<void> => {
  const response = await fetch(`${API_BASE}/workflows/${workflowId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update workflow');
  }
};

export const deleteWorkflow = async (workflowId: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/workflows/${workflowId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete workflow');
  }
};

export const getWorkflowTemplates = async (): Promise<{ templates: any[] }> => {
  const response = await fetch(`${API_BASE}/workflows/templates`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workflow templates');
  }

  return response.json();
};

export const applyWorkflowTemplate = async (eventTypeId: number, templateName: string): Promise<{ id: number }> => {
  const response = await fetch(`${API_BASE}/workflows/event-type/${eventTypeId}/apply-template`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ templateName })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to apply template');
  }

  return response.json();
};
